<template>
  <div class="app-container">
    <!-- Navigation Bar with Steps -->
    <div class="navigate-bar">
        <span class="page-title">Dataset Upload</span>
        <div class="steps-container">
          <el-steps :active="currentStep" finish-status="success" align-center class="clickable-steps">
            <el-step title="Privacy" icon="el-icon-view" @click.native="goToStep(0)"></el-step>
            <el-step title="Metadata" icon="el-icon-document" @click.native="goToStep(1)"></el-step>
            <el-step title="Upload Files" icon="el-icon-upload" @click.native="goToStep(2)"></el-step>
            <el-step title="Quality Control" icon="el-icon-data-analysis" @click.native="goToStep(3)"></el-step>
          </el-steps>
        </div>
    </div>

    <!-- Content Section -->
    <div class="content-section">
      <el-card>
        <div class="form-body">
          <!-- Step 1: Dataset Type Selection -->
          <div v-show="currentStep === 0">
            <div class="step-header">
              <h3>Step 1: Select Dataset Type</h3>
              <p>Choose the type of dataset you want to upload</p>
            </div>
            <div class="field privacy-selection">
              <div class="label" style="display: inline-block;">Select dataset type:</div>
              <el-radio-group v-model="form.datasetType">
                <el-radio style="border-color: green; color: green;" :label="'public'">
                  <span><i class="el-icon-view"></i> Public Dataset</span>
                </el-radio>
                <el-radio style="border-color: goldenrod; color: goldenrod;" :label="'private'">
                  <span><i class="el-icon-lock"></i> Private Dataset</span>
                </el-radio>
                <el-radio style="border-color: #409EFF; color: #409EFF;" :label="'differential'">
                  <span><i class="el-icon-s-data"></i> Differential Data</span>
                </el-radio>
              </el-radio-group>
              <div v-if="form.datasetType === 'differential'" style="margin-top: 12px; padding: 12px; background: #ecf5ff; border: 1px solid #b3d8ff; border-radius: 4px; color: #606266; font-size: 14px;">
                <i class="el-icon-info" style="color: #409EFF;"></i>
                <strong>Differential Data:</strong> Upload a differential expression table with columns like p-value, FDR, fold change, etc. Column names can be flexible. This dataset type focuses on displaying the differential expression results and enables gene selection for protein function and enrichment analysis.
              </div>
            </div>
            <div class="step-actions">
              <el-button type="primary" @click="nextStep" :disabled="form.datasetType === null">Next Step</el-button>
            </div>
          </div>

          <!-- Step 2: Metadata -->
          <div v-show="currentStep === 1">
            <div class="step-header">
              <h3>Step 2: Dataset Metadata</h3>
              <p v-if="form.datasetType === 'public'">Enter or load publication information from PubMed</p>
              <p v-else-if="form.datasetType === 'differential'">Enter publication information - PubMed ID is required for differential data</p>
              <p v-else>Provide basic information about your dataset</p>
            </div>

            <template v-if="form.datasetType === 'public'">
              <div class="field">
                <div class="label">PubMed ID</div>
                <div class="row-line">
                  <el-input v-model="form.pubmedId" placeholder="e.g. 30093607" clearable />
                  <el-button class="modern-btn" icon="el-icon-search" :loading="pubmedLoading" @click="fetchPubmed">Load from PubMed</el-button>
                  <el-button class="manual-btn" icon="el-icon-edit" @click="enableManualInput">Manual Input</el-button>
                  <el-upload
                    ref="jsonUpload"
                    action="#"
                    :auto-upload="false"
                    :on-change="handleJsonUpload"
                    :show-file-list="false"
                    accept=".json,.txt"
                    style="display: inline-block;">
                    <el-button class="json-btn" icon="el-icon-document">Load from File</el-button>
                  </el-upload>
                </div>
              </div>
              <div class="field" v-if="pubmedLoaded || manualInput">
                <div class="label">Title <span class="required-star">*</span></div>
                <el-input v-model="form.name" placeholder="Enter publication title (required)" />
              </div>
              <div class="field" v-if="pubmedLoaded || manualInput">
                <div class="label">Abstract <span class="required-star">*</span></div>
                <el-input v-model="form.description" type="textarea" :rows="6" placeholder="Enter publication abstract (required)" />
              </div>
            </template>

            <template v-else-if="form.datasetType === 'differential'">
              <div class="field">
                <div class="label">PubMed ID <span class="required-star">*</span></div>
                <div class="row-line">
                  <el-input v-model="form.pubmedId" placeholder="e.g. 30093607 (required)" clearable />
                  <el-button class="modern-btn" icon="el-icon-search" :loading="pubmedLoading" @click="fetchPubmed">Load from PubMed</el-button>
                  <el-button class="manual-btn" icon="el-icon-edit" @click="enableManualInput">Manual Input</el-button>
                  <el-upload
                    ref="jsonUploadDifferential"
                    action="#"
                    :auto-upload="false"
                    :on-change="handleJsonUpload"
                    :show-file-list="false"
                    accept=".json,.txt"
                    style="display: inline-block;">
                    <el-button class="json-btn" icon="el-icon-document">Load from File</el-button>
                  </el-upload>
                </div>
              </div>
              <div class="field" v-if="pubmedLoaded || manualInput">
                <div class="label">Title <span class="required-star">*</span></div>
                <el-input v-model="form.name" placeholder="Enter publication title (required)" />
              </div>
              <div class="field" v-if="pubmedLoaded || manualInput">
                <div class="label">Abstract <span class="required-star">*</span></div>
                <el-input v-model="form.description" type="textarea" :rows="6" placeholder="Enter publication abstract (required)" />
              </div>
            </template>

            <template v-else-if="form.datasetType === 'private'">
              <div class="field" style="text-align: right;">
                <el-upload
                  ref="jsonUploadPrivate"
                  action="#"
                  :auto-upload="false"
                  :on-change="handleJsonUpload"
                  :show-file-list="false"
                  accept=".json,.txt"
                  style="display: inline-block;">
                  <el-button class="json-btn" icon="el-icon-document" size="small">Load from File</el-button>
                </el-upload>
              </div>
              <div class="field">
                <div class="label">Name <span class="required-star">*</span></div>
                <el-input v-model="form.name" placeholder="Dataset title (required)" />
              </div>
              <div class="field">
                <div class="label">Description <span class="required-star">*</span></div>
                <el-input v-model="form.description" type="textarea" :rows="6" placeholder="Brief summary or background (required)" />
              </div>
            </template>

            <div class="field">
              <div class="label">Cancer Type <span class="required-star">*</span></div>
              <div class="row-line">
                <el-select v-model="form.cancerType" placeholder="Select cancer type (required)" filterable clearable style="flex: 0.5;">
                  <el-option v-for="c in cancerTypeOptions" :key="c" :label="c" :value="c" />
                </el-select>
                <el-button 
                  icon="el-icon-plus" 
                  size="small" 
                  type="primary"
                  @click="showAddCancerTypeDialog"
                >
                  Add New
                </el-button>
                <el-button 
                  icon="el-icon-edit" 
                  size="small" 
                  type="default"
                  @click="showManageCancerTypesDialog"
                >
                  Manage
                </el-button>
              </div>
            </div>
            <div class="field">
              <div class="label">Tissue Type <span class="required-star">*</span></div>
              <div class="row-line">
                <el-select v-model="form.tissueType" placeholder="Select tissue type (required)" filterable clearable style="flex: 0.5;">
                  <el-option v-for="t in tissueTypeOptions" :key="t" :label="t" :value="t" />
                </el-select>
                <el-button 
                  icon="el-icon-plus" 
                  size="small" 
                  type="primary"
                  @click="showAddTissueTypeDialog"
                >
                  Add New
                </el-button>
                <el-button 
                  icon="el-icon-edit" 
                  size="small" 
                  type="default"
                  @click="showManageTissueTypesDialog"
                >
                  Manage
                </el-button>
              </div>
            </div>

            <!-- Paired Analysis Checkbox Removed -->

            <div class="step-actions">
              <el-button @click="prevStep">Previous</el-button>
              <el-button type="primary" @click="nextStep" :disabled="!canProceedFromStep2">
                Next Step
                <span v-if="!canProceedFromStep2" style="margin-left: 4px;">(All fields required)</span>
              </el-button>
            </div>
          </div>

          <!-- Step 3: File Upload -->
          <div v-show="currentStep === 2">
            <div class="step-header">
              <h3>Step 3: Upload Data Files</h3>
              <p v-if="form.datasetType !== 'differential'">Upload and validate your expression matrix, observation metadata, and protein metadata files</p>
              <p v-else>Upload your differential expression table (tab-delimited format)</p>
            </div>

            <div v-if="form.datasetType === 'differential'" class="file-upload-section">
              <!-- Differential Expression File Upload -->
              <el-alert
                v-if="fileErrors.length > 0"
                title="File Validation Errors"
                type="error"
                :closable="false"
                show-icon
                style="margin-bottom: 16px;"
              >
                <div v-for="(err, idx) in fileErrors" :key="idx" class="error-item">{{ err }}</div>
              </el-alert>
              
              <div class="file-upload-item">
                <div class="file-upload-header">
                  <span class="file-label">Differential Expression Table (txt file)</span>
                  <el-tag :type="expFileStatus.type" size="small" v-if="expFileStatus.text">
                    {{ expFileStatus.text }}
                  </el-tag>
                </div>
                <el-upload
                  ref="diffExpUpload"
                  drag
                  action="#"
                  :auto-upload="false"
                  :on-change="handleDiffExpChange"
                  :on-remove="handleDiffExpRemove"
                  :file-list="diffExpFileList"
                  :limit="1"
                  accept=".txt,.tsv,.csv"
                  style="width: 100%;"
                >
                  <i class="el-icon-upload"></i>
                  <div class="el-upload__text">
                    <span>Drop file here or <em>click to upload differential expression table</em></span>
                  </div>
                </el-upload>
                <el-button
                  style="margin-top: 12px;"
                  type="primary"
                  icon="el-icon-check"
                  :loading="expChecking"
                  :disabled="!diffExpFile || expFileStatus.valid"
                  @click="checkDiffExpFile"
                >
                  Check File
                </el-button>
                <div v-if="expFileStatus.shape" class="file-info">
                  Shape: {{ expFileStatus.shape[0] }} genes × {{ expFileStatus.shape[1] }} columns
                </div>
              </div>
              
              <div class="step-actions" style="margin-top: 24px;">
                <el-button @click="prevStep">Previous</el-button>
                <el-button
                  type="primary"
                  icon="el-icon-upload"
                  :disabled="!expFileStatus.valid"
                  :loading="uploading"
                  @click="uploadDataset"
                >
                  Upload Dataset
                </el-button>
              </div>
            </div>

            <div v-else class="file-upload-section">
              <div style="display: flex; align-items: center; justify-content: space-between;margin: 20px;">
                <div class="field-hint" style="margin: 0; flex: 1;">
                  <i class="el-icon-info"></i> Use this button to reset all files if you need to upload different files
                </div>
                <el-button
                  type="warning"
                  icon="el-icon-refresh-left"
                  @click="resetAllFiles"
                  :disabled="!expFile && !obsFile && !varFile"
                >
                  Reset All Files
                </el-button>
              </div>
              <div class="label">Data Files</div>
            <!-- File Check Errors Display -->
            <el-alert
              v-if="fileErrors.length > 0"
              title="File Validation Errors"
              type="error"
              :closable="false"
              show-icon
              style="margin-bottom: 16px;"
            >
              <div v-for="(err, idx) in fileErrors" :key="idx" class="error-item">{{ err }}</div>
            </el-alert>

            <!-- Expression File Upload -->
            <div class="file-upload-item">
              <div class="file-upload-header">
                <span class="file-label">1. Expression Matrix (exp.txt)</span>
                <el-tag :type="expFileStatus.type" size="small" v-if="expFileStatus.text">
                  {{ expFileStatus.text }}
                </el-tag>
              </div>
              <el-upload
                ref="expUpload"
                drag
                action="#"
                :auto-upload="false"
                :on-change="handleExpChange"
                :on-remove="handleExpRemove"
                :file-list="expFileList"
                :limit="1"
                accept=".txt"
                style="width: 100%;"
                :disabled="expFileStatus.valid"
              >
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">
                  <span v-if="form.datasetType === 'public' && form.pubmedId" style="color: red;font-size: 16px;border: 1px solid red;padding: 10px;margin-right: 20px;border-radius: 4px;"> PubMed ID: {{ form.pubmedId }}</span>
                  <span v-if="!expFileStatus.valid">Drop file here or <em>click to upload expression matrix</em></span>
                  <span v-else style="color: #67C23A;"><i class="el-icon-success"></i> Expression file validated and locked</span>
                </div>
              </el-upload>
              <el-button
                style="margin-top: 12px;"
                type="primary"
                icon="el-icon-check"
                :loading="expChecking"
                :disabled="!expFile || expFileStatus.valid"
                @click="checkExpFile"
              >
                Check File
              </el-button>
              <div v-if="expFileStatus.shape" class="file-info">
                Shape: {{ expFileStatus.shape[0] }} samples × {{ expFileStatus.shape[1] }} proteins
              </div>
            </div>

            <!-- Obs File Upload (shown only after exp is valid) -->
            <div class="file-upload-item" v-if="expFileStatus.valid">
              <div class="file-upload-header">
                <span class="file-label">2. Clinical Metadata (obs.txt)</span>
                <el-tag :type="obsFileStatus.type" size="small" v-if="obsFileStatus.text">
                  {{ obsFileStatus.text }}
                </el-tag>
              </div>
              <el-upload
                ref="obsUpload"
                drag
                action="#"
                :auto-upload="false"
                :on-change="handleObsChange"
                :on-remove="handleObsRemove"
                :file-list="obsFileList"
                :limit="1"
                accept=".txt"
                style="width: 100%;"
              >
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">
                  <span v-if="form.datasetType === 'public' && form.pubmedId" style="color: red;font-size: 16px;border: 1px solid red;padding: 10px;margin-right: 20px;border-radius: 4px;"> PubMed ID: {{ form.pubmedId }}</span>
                  <span>Drop file here or <em>click to upload clinical metadata</em></span>
                </div>
              </el-upload>
              <el-button
                style="margin-top: 12px;"
                type="primary"
                icon="el-icon-check"
                :loading="obsChecking"
                :disabled="!obsFile || obsFileStatus.valid"
                @click="checkObsFile"
              >
                Check File
              </el-button>
              <div v-if="obsFileStatus.shape" class="file-info">
                Shape: {{ obsFileStatus.shape[0] }} samples × {{ obsFileStatus.shape[1] }} variables
              </div>
            </div>

            <!-- Var File Upload (shown only after exp is valid) -->
            <div class="file-upload-item" v-if="expFileStatus.valid">
              <div class="file-upload-header">
                <span class="file-label">3. Protein Metadata (protein.txt)</span>
                <el-tag :type="varFileStatus.type" size="small" v-if="varFileStatus.text">
                  {{ varFileStatus.text }}
                </el-tag>
              </div>
              <el-upload
                ref="varUpload"
                drag
                action="#"
                :auto-upload="false"
                :on-change="handleVarChange"
                :on-remove="handleVarRemove"
                :file-list="varFileList"
                :limit="1"
                accept=".txt"
                style="width: 100%;"
              >
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">
                  <span v-if="form.datasetType === 'public' && form.pubmedId" style="color: red;font-size: 16px;border: 1px solid red;padding: 10px;margin-right: 20px;border-radius: 4px;"> PubMed ID: {{ form.pubmedId }}</span>
                  <span>Drop file here or <em>click to upload protein metadata</em></span>
                </div>
              </el-upload>
              <el-button
                style="margin-top: 12px;"
                type="primary"
                icon="el-icon-check"
                :loading="varChecking"
                :disabled="!varFile || varFileStatus.valid"
                @click="checkVarFile"
              >
                Check File
              </el-button>
              <div v-if="varFileStatus.shape" class="file-info">
                Shape: {{ varFileStatus.shape[0] }} proteins × {{ varFileStatus.shape[1] }} columns
              </div>
            </div>

            <!-- Slice File Upload (shown only for private datasets) -->
            <div class="file-upload-item" v-if="form.datasetType === 'private'">
              <div class="file-upload-header">
                <span class="file-label">
                  4. Slice Images (slice.zip) 
                  <el-tag type="info" size="mini" style="margin-left: 8px;">Optional</el-tag>
                </span>
              </div>
              <div class="slice-instructions" style="margin-bottom: 12px; padding: 12px; background: #f5f7fa; border-radius: 4px; font-size: 13px;">
                <div style="margin-bottom: 8px;"><strong>ZIP Structure Requirements:</strong></div>
                <div style="margin-left: 16px;">
                  <div>• Archive name: <code>parent_directory.zip</code></div>
                  <div>• Structure: <code>parent_directory/40x/sampleID.jpg</code></div>
                  <div>• Structure: <code>parent_directory/10x/sampleID.jpg</code></div>
                  <div>• Supported formats: .jpg, .jpeg, .png, .tif, .tiff</div>
                  <div>• Magnifications should be folders like: 40x, 10x, 20x, etc.</div>
                </div>
              </div>
              <el-upload
                ref="sliceUpload"
                drag
                action="#"
                :auto-upload="false"
                :on-change="handleSliceChange"
                :on-remove="handleSliceRemove"
                :file-list="sliceFileList"
                :limit="1"
                accept=".zip"
                style="width: 100%;"
              >
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">
                  Drop ZIP file here or <em>click to upload slice images</em>
                </div>
              </el-upload>
            </div>
            </div>

            <!-- Basic Info Section -->
            <div class="field" style="margin-top: 16px;">
              <div class="label">
                Basic Info
                <span v-if="false" style="font-size: 12px; color: #909399; font-weight: normal; margin-left: 8px;">
                  <i class="el-icon-rank"></i> Drag to reorder
                </span>
              </div>
              <div>
                <el-tag
                  v-for="(item, idx) in form.basicInfo"
                  :key="idx"
                  closable
                  @close="removeBasicInfo(idx)"
                  draggable
                  @dragstart="handleBasicInfoDragStart(idx, $event)"
                  @dragover.prevent="handleBasicInfoDragOver(idx, $event)"
                  @drop="handleBasicInfoDrop(idx, $event)"
                  @dragend="handleBasicInfoDragEnd"
                  :class="{ 'dragging': draggingBasicInfoIndex === idx }"
                  class="draggable-tag"
                  style="margin-right: 8px; margin-bottom: 8px; cursor: move;"
                >
                  {{ item.key }}: {{ item.value }}
                </el-tag>
                <el-button size="small" icon="el-icon-plus" @click="showBasicInfoDialog = true">Add</el-button>
              </div>
            </div>

            <div class="step-actions">
              <el-button @click="prevStep">Previous</el-button>
              <el-button
                type="primary"
                icon="el-icon-right"
                :disabled="!allFilesValid"
                @click="proceedToQC"
              >
                Proceed to Quality Control
              </el-button>
            </div>
          </div>

          <!-- Step 4: Quality Control -->
          <div v-show="currentStep === 3">
            <div class="step-header">
              <h3>Step 4: Quality Control</h3>
              <p>Review data quality and configure filtering parameters</p>
            </div>

            <!-- QC Loading -->
            <div v-if="qcLoading" style="text-align: center; padding: 40px;">
              <i class="el-icon-loading" style="font-size: 40px; color: #409EFF;"></i>
              <p style="margin-top: 16px; color: #909399;">Analyzing data quality...</p>
            </div>

            <!-- QC Content -->
            <div v-else-if="qcStats">
              <!-- Data Overview -->
              <el-card class="qc-section" shadow="never">
                <div slot="header" class="qc-section-header">
                  <i class="el-icon-data-line"></i>
                  <span>Data Overview</span>
                </div>
                <div class="qc-stats-grid">
                  <div class="qc-stat-item">
                    <div class="qc-stat-label">Samples</div>
                    <div class="qc-stat-value">{{ qcStats.n_cells }}</div>
                  </div>
                  <div class="qc-stat-item">
                    <div class="qc-stat-label">Proteins</div>
                    <div class="qc-stat-value">{{ qcStats.n_genes }}</div>
                  </div>
                </div>
              </el-card>

              <!-- QC Histograms and Filtering -->
              <el-card class="qc-section" shadow="never">
                <div slot="header" class="qc-section-header qc-section-header-important">
                  <i class="el-icon-s-data"></i>
                  <span>Quality Metrics Distribution & Filtering</span>
                  <el-tag type="warning" size="small" style="margin-left: 12px;">Configure Before Upload</el-tag>
                </div>

                <!-- Distribution Charts -->
                <div class="qc-charts-container">
                  <div class="qc-chart">
                    <div class="qc-chart-title">
                      Total Counts per Sample {{ applyLog1p ? '(from log1p matrix)' : '' }}
                    </div>
                    <Plot
                      v-if="totalCountsPlot.data.length > 0"
                      :data="totalCountsPlot.data"
                      :layout="totalCountsPlot.layout"
                      :config="{ responsive: true, displayModeBar: false }"
                      style="width: 100%; height: 300px;"
                    />
                    <div v-else class="plot-placeholder" style="height: 300px; display: flex; align-items: center; justify-content: center; color: #999;">
                      No data available
                    </div>
                  </div>
                  <div class="qc-chart">
                    <div class="qc-chart-title">
                      Dropout Percentage per Protein
                    </div>
                    <Plot
                      v-if="dropoutPlot.data.length > 0"
                      :data="dropoutPlot.data"
                      :layout="dropoutPlot.layout"
                      :config="{ responsive: true, displayModeBar: false }"
                      style="width: 100%; height: 300px;"
                    />
                    <div v-else class="plot-placeholder" style="height: 300px; display: flex; align-items: center; justify-content: center; color: #999;">
                      No data available
                    </div>
                  </div>
                </div>

                <!-- Transformation Options -->
                <div style="margin-top: 20px; padding: 12px; background: #f0f9ff; border-radius: 8px; border: 1px solid #3b82f6;">
                  <el-checkbox v-model="applyLog1p" @change="updateChartsWithLog">
                    <strong style="color: #1e40af;">Apply log1p transformation</strong>
                  </el-checkbox>
                  <div class="field-hint" style="margin-left: 24px; margin-top: 4px;">
                    Log transformation helps visualize data with large dynamic ranges and makes distributions more normal-like. This applies to both the charts above and the uploaded data.
                  </div>
                </div>

                <!-- Filtering Preview (right under plots) -->
                <el-alert
                  v-if="qcPreview"
                  type="info"
                  :closable="false"
                  show-icon
                  style="margin-top: 20px;"
                >
                  <template slot="title">
                    <strong>Filtering Preview</strong>
                  </template>
                  <div class="qc-preview-content">
                    <div class="qc-preview-item">
                      <span>Samples: </span>
                      <span class="qc-preview-retain">{{ qcPreview.cells_retained }} retained</span>
                      <span class="qc-preview-remove"> ({{ qcPreview.cells_removed }} removed)</span>
                    </div>
                    <div class="qc-preview-item">
                      <span>Proteins: </span>
                      <span class="qc-preview-retain">{{ qcPreview.genes_retained }} retained</span>
                      <span class="qc-preview-remove"> ({{ qcPreview.genes_removed }} removed)</span>
                    </div>
                  </div>
                </el-alert>
                <!-- Filtering Parameters (under log1p) -->
                <div class="qc-params-container" style="margin-top: 20px;">
                  <div style="margin-bottom: 16px; color: #1e40af; font-weight: 600; font-size: 15px;">
                    <i class="el-icon-setting"></i> Filtering Parameters
                  </div>
                  <el-form label-width="200px">
                    <el-form-item label="Min Proteins per Sample" class="qc-form-item">
                      <div class="qc-input-row">
                        <el-input-number 
                          v-model="qcParams.min_genes" 
                          :min="0" 
                          :max="10000" 
                          @change="previewQCFilter"
                          size="medium"
                        />
                        <span class="param-hint-inline">Remove samples with fewer than this many detected proteins</span>
                      </div>
                    </el-form-item>
                    <el-form-item label="Min Samples per Protein" class="qc-form-item">
                      <div class="qc-input-row">
                        <el-input-number 
                          v-model="qcParams.min_cells" 
                          :min="0" 
                          :max="1000" 
                          @change="previewQCFilter"
                          size="medium"
                        />
                        <span class="param-hint-inline">Remove proteins detected in fewer than this many samples</span>
                      </div>
                    </el-form-item>
                    <el-form-item label="Max Dropout (%)" class="qc-form-item">
                      <div class="qc-input-row">
                        <el-input-number 
                          v-model="qcParams.max_dropout" 
                          :min="0" 
                          :max="100" 
                          @change="previewQCFilter"
                          size="medium"
                        />
                        <span class="param-hint-inline">Remove proteins with dropout percentage above this threshold</span>
                      </div>
                    </el-form-item>
                  </el-form>
                </div>
              </el-card>

              <!-- Actions -->
              <div class="step-actions">
                <el-button @click="prevStep">Previous</el-button>
                
                <!-- Upload Progress Bar (inline, minimal) -->
                <div v-if="uploading" class="upload-progress-inline">
                  <span class="progress-text">{{ uploadProgressText }}</span>
                  <el-progress 
                    :percentage="uploadProgress" 
                    :status="uploadProgress === 100 ? 'success' : ''"
                    :stroke-width="6"
                    style="width: 200px;"
                  ></el-progress>
                </div>
                
                <el-button
                  type="success"
                  icon="el-icon-upload"
                  :loading="uploading"
                  @click="uploadDataset"
                >
                  Upload Dataset
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- Basic Info Dialog -->
    <el-dialog title="Add Basic Information" :visible.sync="showBasicInfoDialog" width="500px">
      <el-form label-width="80px">
        <el-form-item label="Key">
          <el-input v-model="basicInfoKey" placeholder="e.g., Sample Size" />
        </el-form-item>
        <el-form-item label="Value">
          <el-input v-model="basicInfoValue" placeholder="e.g., 100" @keyup.enter.native="addBasicInfo" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button type="primary" @click="addBasicInfo">Add</el-button>
      </span>
    </el-dialog>

    <!-- Add Cancer Type Dialog -->
    <el-dialog title="Add New Cancer Type" :visible.sync="addCancerTypeDialog" width="500px">
      <el-form label-width="140px">
        <el-form-item label="Abbreviation">
          <el-input 
            v-model="newCancerTypeAbbr" 
            placeholder="Enter abbreviation (e.g., BRCA)" 
            @input="handleCancerAbbrInput"
            maxlength="10"
          />
          <div v-if="cancerAbbrExists" style="color: #F56C6C; font-size: 12px; margin-top: 4px;">
            ⚠️ This abbreviation already exists in the list
          </div>
        </el-form-item>
        <el-form-item label="Full Name">
          <el-input 
            v-model="newCancerTypeFullName" 
            placeholder="Enter full name (e.g., Breast Invasive Carcinoma)" 
            @keyup.enter.native="addNewCancerType"
          />
          <div v-if="cancerFullNameExists" style="color: #F56C6C; font-size: 12px; margin-top: 4px;">
            ⚠️ This full name already exists in the list
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="addCancerTypeDialog = false">Cancel</el-button>
        <el-button 
          type="primary" 
          @click="addNewCancerType" 
          :loading="addingCancerType"
          :disabled="!canAddCancerType"
        >
          Add
        </el-button>
      </span>
    </el-dialog>

    <!-- Add Tissue Type Dialog -->
    <el-dialog title="Add New Tissue Type" :visible.sync="addTissueTypeDialog" width="500px">
      <el-form label-width="120px">
        <el-form-item label="Tissue Type">
          <el-input 
            v-model="newTissueType" 
            placeholder="Enter tissue type name (e.g., Liver)" 
            @keyup.enter.native="addNewTissueType"
            autofocus
          />
          <div v-if="tissueNameExists" style="color: #F56C6C; font-size: 12px; margin-top: 4px;">
            ⚠️ This tissue type already exists in the list
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="addTissueTypeDialog = false">Cancel</el-button>
        <el-button 
          type="primary" 
          @click="addNewTissueType" 
          :loading="addingTissueType"
          :disabled="!canAddTissueType"
        >
          Add
        </el-button>
      </span>
    </el-dialog>

    <!-- Manage Cancer Types Dialog -->
    <el-dialog 
      title="Manage Cancer Types" 
      :visible.sync="manageCancerTypesDialog" 
      width="1000px" 
      custom-class="manage-cancer-types-dialog"
      top="5vh"
    >
      <div style="margin-bottom: 12px; color: #606266;">
        Edit cancer types below. Abbreviations will be auto-capitalized and must be unique. Full names must also be unique.
      </div>
      
      <!-- Validation errors -->
      <el-alert
        v-if="cancerTypeEditErrors.length > 0"
        title="Validation Errors"
        type="error"
        :closable="false"
        style="margin-bottom: 12px;"
      >
        <div v-for="(err, idx) in cancerTypeEditErrors" :key="idx">{{ err }}</div>
      </el-alert>

      <el-table :data="editableCancerTypes" border style="width: 100%;" :max-height="tableMaxHeight">
        <el-table-column label="Abbreviation" width="180">
          <template slot-scope="scope">
            <el-input 
              v-model="scope.row.abbr" 
              placeholder="e.g., BRCA"
              @input="(value) => handleEditCancerAbbrInput(scope.$index, value)"
              maxlength="10"
            />
          </template>
        </el-table-column>
        <el-table-column label="Full Name">
          <template slot-scope="scope">
            <el-input 
              v-model="scope.row.fullName" 
              placeholder="e.g., Breast Invasive Carcinoma"
            />
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="100" align="center">
          <template slot-scope="scope">
            <el-button 
              type="danger" 
              icon="el-icon-delete" 
              size="mini" 
              circle
              @click="removeCancerTypeFromEdit(scope.$index)"
            ></el-button>
          </template>
        </el-table-column>
      </el-table>

      <span slot="footer">
        <el-button @click="manageCancerTypesDialog = false">Cancel</el-button>
        <el-button 
          type="primary" 
          @click="saveCancerTypes" 
          :loading="savingCancerTypes"
          :disabled="cancerTypeEditErrors.length > 0"
        >
          Save Changes
        </el-button>
      </span>
    </el-dialog>

    <!-- Manage Tissue Types Dialog -->
    <el-dialog 
      title="Manage Tissue Types" 
      :visible.sync="manageTissueTypesDialog" 
      width="1000px" 
      custom-class="manage-tissue-types-dialog"
      top="5vh"
    >
      <div style="margin-bottom: 12px; color: #606266;">
        Edit tissue types below. Each tissue type must be unique.
      </div>
      
      <!-- Validation errors -->
      <el-alert
        v-if="tissueTypeEditErrors.length > 0"
        title="Validation Errors"
        type="error"
        :closable="false"
        style="margin-bottom: 12px;"
      >
        <div v-for="(err, idx) in tissueTypeEditErrors" :key="idx">{{ err }}</div>
      </el-alert>

      <el-table :data="editableTissueTypes" border style="width: 100%;" :max-height="tableMaxHeight">
        <el-table-column label="Tissue Type">
          <template slot-scope="scope">
            <el-input 
              v-model="scope.row.name" 
              placeholder="e.g., Liver"
            />
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="100" align="center">
          <template slot-scope="scope">
            <el-button 
              type="danger" 
              icon="el-icon-delete" 
              size="mini" 
              circle
              @click="removeTissueTypeFromEdit(scope.$index)"
            ></el-button>
          </template>
        </el-table-column>
      </el-table>

      <span slot="footer">
        <el-button @click="manageTissueTypesDialog = false">Cancel</el-button>
        <el-button 
          type="primary" 
          @click="saveTissueTypes" 
          :loading="savingTissueTypes"
          :disabled="tissueTypeEditErrors.length > 0"
        >
          Save Changes
        </el-button>
      </span>
    </el-dialog>
  </div>
  
</template>

<script>
import Plot from '@/components/VuePlotly/index-plotly.vue';

export default {
  name: 'UploadView',
  components: {
    Plot
  },
  data() {
    return {
      currentStep: 0,
      form: {
        name: '',
        description: '',
        pubmedId: '',
        cancerType: '',
        tissueType: '',
        datasetType: null, // 'public', 'private', or 'differential'
        basicInfo: []
      },
      pubmedLoaded: false,
      pubmedLoading: false,
      manualInput: false,
      showBasicInfoDialog: false,
      basicInfoKey: '',
      basicInfoValue: '',
      cancerTypeOptions: [],
      tissueTypeOptions: [],
      // isPairedAnalysis removed
      
      // Add controlled list dialogs
      addCancerTypeDialog: false,
      addTissueTypeDialog: false,
      newCancerType: '',
      newTissueType: '',
      newCancerTypeAbbr: '',
      newCancerTypeFullName: '',
      addingCancerType: false,
      addingTissueType: false,
      
      // Manage cancer types dialog
      manageCancerTypesDialog: false,
      editableCancerTypes: [],
      savingCancerTypes: false,
      
      // Manage tissue types dialog
      manageTissueTypesDialog: false,
      editableTissueTypes: [],
      savingTissueTypes: false,
      
      // File upload states
      expFile: null,
      obsFile: null,
      varFile: null,
      sliceFile: null,
      diffExpFile: null, // For differential data type
      expFileList: [],
      obsFileList: [],
      varFileList: [],
      sliceFileList: [],
      diffExpFileList: [],
      expFileStatus: { valid: false, type: '', text: '', shape: null },
      obsFileStatus: { valid: false, type: '', text: '', shape: null },
      varFileStatus: { valid: false, type: '', text: '', shape: null },
      expChecking: false,
      obsChecking: false,
      varChecking: false,
      uploading: false,
      uploadProgress: 0,
      uploadProgressText: 'Preparing upload...',
      fileErrors: [],
      
      // QC states
      qcLoading: false,
      qcStats: null,
      applyLog1p: false,
      qcParams: {
        min_genes: 200,
        min_cells: 3,
        max_dropout: 60
      },
      qcPreview: null,
      // Plotly chart data
      totalCountsPlot: { data: [], layout: {} },
      dropoutPlot: { data: [], layout: {} },
      
      // Drag and drop state for basic info
      draggingBasicInfoIndex: null,
      draggedBasicInfoItem: null
    }
  },
  computed: {
    allFilesValid() {
      return this.expFileStatus.valid && 
             this.obsFileStatus.valid && 
             this.varFileStatus.valid;
    },
    canProceedFromStep2() {
      // All fields are required: name, description, cancerType, tissueType
      const hasBasicFields = this.form.name && 
                            this.form.name.trim() !== '' &&
                            this.form.description && 
                            this.form.description.trim() !== '' &&
                            this.form.cancerType && 
                            this.form.cancerType.trim() !== '' &&
                            this.form.tissueType && 
                            this.form.tissueType.trim() !== '';
      
      if (this.form.datasetType === 'public' || this.form.datasetType === 'differential') {
        // Both public and differential require PubMed ID
        return (this.pubmedLoaded || this.manualInput) && hasBasicFields;
      } else {
        return hasBasicFields;
      }
    },
    // Cancer type validation
    cancerAbbrExists() {
      if (!this.newCancerTypeAbbr || !this.newCancerTypeAbbr.trim()) return false;
      const abbr = this.newCancerTypeAbbr.trim().toUpperCase();
      return this.cancerTypeOptions.some(option => {
        // Support both formats: "ABBR - Full Name" and "ABBR_FullName"
        let parts;
        if (option.includes(' - ')) {
          parts = option.split(' - ');
        } else if (option.includes('_')) {
          parts = option.split('_');
        } else {
          parts = [option];
        }
        return parts.length > 0 && parts[0].toUpperCase() === abbr;
      });
    },
    cancerFullNameExists() {
      if (!this.newCancerTypeFullName || !this.newCancerTypeFullName.trim()) return false;
      const fullName = this.newCancerTypeFullName.trim().toLowerCase();
      return this.cancerTypeOptions.some(option => {
        // Support both formats: "ABBR - Full Name" and "ABBR_FullName"
        let parts;
        if (option.includes(' - ')) {
          parts = option.split(' - ');
        } else if (option.includes('_')) {
          parts = option.split('_');
        } else {
          parts = [option];
        }
        return parts.length > 1 && parts[1].toLowerCase() === fullName;
      });
    },
    canAddCancerType() {
      return this.newCancerTypeAbbr && 
             this.newCancerTypeAbbr.trim() !== '' &&
             this.newCancerTypeFullName && 
             this.newCancerTypeFullName.trim() !== '' &&
             !this.cancerAbbrExists &&
             !this.cancerFullNameExists;
    },
    // Tissue type validation
    tissueNameExists() {
      if (!this.newTissueType || !this.newTissueType.trim()) return false;
      const tissueName = this.newTissueType.trim().toLowerCase();
      return this.tissueTypeOptions.some(option => 
        option.toLowerCase() === tissueName
      );
    },
    canAddTissueType() {
      return this.newTissueType && 
             this.newTissueType.trim() !== '' &&
             !this.tissueNameExists;
    },
    // Cancer type edit validation
    cancerTypeEditErrors() {
      const errors = [];
      const seenAbbrs = new Map();
      const seenFullNames = new Map();
      
      this.editableCancerTypes.forEach((item, index) => {
        // Check for empty fields
        if (!item.abbr || !item.abbr.trim()) {
          errors.push(`Row ${index + 1}: Abbreviation is required`);
        }
        if (!item.fullName || !item.fullName.trim()) {
          errors.push(`Row ${index + 1}: Full name is required`);
        }
        
        // Check for duplicate abbreviations
        if (item.abbr && item.abbr.trim()) {
          const abbrUpper = item.abbr.trim().toUpperCase();
          if (seenAbbrs.has(abbrUpper)) {
            errors.push(`Row ${index + 1}: Abbreviation "${item.abbr}" is duplicated (also in row ${seenAbbrs.get(abbrUpper) + 1})`);
          } else {
            seenAbbrs.set(abbrUpper, index);
          }
        }
        
        // Check for duplicate full names
        if (item.fullName && item.fullName.trim()) {
          const fullNameLower = item.fullName.trim().toLowerCase();
          if (seenFullNames.has(fullNameLower)) {
            errors.push(`Row ${index + 1}: Full name "${item.fullName}" is duplicated (also in row ${seenFullNames.get(fullNameLower) + 1})`);
          } else {
            seenFullNames.set(fullNameLower, index);
          }
        }
      });
      
      return errors;
    },
    // Tissue type edit validation
    tissueTypeEditErrors() {
      const errors = [];
      const seenNames = new Map();
      
      this.editableTissueTypes.forEach((item, index) => {
        // Check for empty fields
        if (!item.name || !item.name.trim()) {
          errors.push(`Row ${index + 1}: Tissue type name is required`);
        }
        
        // Check for duplicate names
        if (item.name && item.name.trim()) {
          const nameLower = item.name.trim().toLowerCase();
          if (seenNames.has(nameLower)) {
            errors.push(`Row ${index + 1}: Tissue type "${item.name}" is duplicated (also in row ${seenNames.get(nameLower) + 1})`);
          } else {
            seenNames.set(nameLower, index);
          }
        }
      });
      
      return errors;
    },
    tableMaxHeight() {
      // Calculate max height for table: 90vh - (header + description + alert space + footer + padding)
      return Math.floor(window.innerHeight * 0.9 - 250);
    }
  },
  watch: {
    allFilesValid(newVal) {
      if (newVal) {
        this.autoPopulateBasicInfo();
      }
    }
  },
  mounted() {
    this.fetchControlledLists();
  },
  methods: {
    async fetchControlledLists() {
      try {
        const res = await fetch('/api/projectUpload/controlled_lists', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }).then(r => r.json());
        
        if (res && res.code === 200 && res.data) {
          this.cancerTypeOptions = res.data.tcga_names || [];
          this.tissueTypeOptions = res.data.tcga_tissues || [];
        } else {
          console.error('Failed to fetch controlled lists:', res);
          this.$notify({
            title: 'Warning',
            message: 'Failed to load cancer types and tissue types',
            type: 'warning',
            position: 'top-right'
          });
        }
      } catch (e) {
        console.error('Error fetching controlled lists:', e);
        this.$notify({
          title: 'Error',
          message: 'Failed to load cancer types and tissue types: ' + e.message,
          type: 'error',
          position: 'top-right'
        });
      }
    },
    
    showAddCancerTypeDialog() {
      this.newCancerType = '';
      this.newCancerTypeAbbr = '';
      this.newCancerTypeFullName = '';
      this.addCancerTypeDialog = true;
    },
    
    showAddTissueTypeDialog() {
      this.newTissueType = '';
      this.addTissueTypeDialog = true;
    },
    
    showManageCancerTypesDialog() {
      // Parse existing cancer types into editable format
      this.editableCancerTypes = this.cancerTypeOptions.map(option => {
        let abbr = '';
        let fullName = '';
        
        if (option.includes(' - ')) {
          const parts = option.split(' - ');
          abbr = parts[0] || '';
          fullName = parts[1] || '';
        } else if (option.includes('_')) {
          const parts = option.split('_');
          abbr = parts[0] || '';
          fullName = parts[1] || '';
        } else {
          abbr = option;
          fullName = '';
        }
        
        return { abbr, fullName };
      });
      
      this.manageCancerTypesDialog = true;
    },
    
    handleEditCancerAbbrInput(index, value) {
      // Only allow A-Z letters and convert to uppercase
      const filtered = value.replace(/[^A-Za-z]/g, '').toUpperCase();
      this.editableCancerTypes[index].abbr = filtered;
    },
    
    removeCancerTypeFromEdit(index) {
      this.$confirm('Are you sure you want to remove this cancer type?', 'Confirm Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.editableCancerTypes.splice(index, 1);
      }).catch(() => {
        // User cancelled
      });
    },
    
    async saveCancerTypes() {
      // Final validation check
      if (this.cancerTypeEditErrors.length > 0) {
        this.$notify({
          title: 'Validation Error',
          message: 'Please fix all validation errors before saving',
          type: 'error',
          position: 'top-right'
        });
        return;
      }
      
      this.savingCancerTypes = true;
      
      try {
        // Convert editable cancer types back to the format: "ABBR_FullName"
        const cancerTypesList = this.editableCancerTypes.map(item => 
          `${item.abbr.trim()}_${item.fullName.trim()}`
        );
        
        const res = await fetch('/api/projectUpload/update_cancer_types', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cancer_types: cancerTypesList })
        }).then(r => r.json());
        
        if (res && res.code === 200) {
          this.$notify({
            title: 'Success',
            message: 'Cancer types updated successfully',
            type: 'success',
            position: 'top-right'
          });
          
          // Update the options list
          this.cancerTypeOptions = res.data.tcga_names || [];

          // deselect cancer types from el-select
          this.form.cancerType = '';
          
          // Close dialog
          this.manageCancerTypesDialog = false;
        } else {
          this.$notify({
            title: 'Error',
            message: res.error || 'Failed to update cancer types',
            type: 'error',
            position: 'top-right'
          });
        }
      } catch (e) {
        console.error('Error updating cancer types:', e);
        this.$notify({
          title: 'Error',
          message: 'Failed to update cancer types',
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.savingCancerTypes = false;
      }
    },
    
    showManageTissueTypesDialog() {
      // Copy existing tissue types into editable format
      this.editableTissueTypes = this.tissueTypeOptions.map(option => {
        return { name: option };
      });
      
      this.manageTissueTypesDialog = true;
    },
    
    removeTissueTypeFromEdit(index) {
      this.$confirm('Are you sure you want to remove this tissue type?', 'Confirm Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.editableTissueTypes.splice(index, 1);
      }).catch(() => {
        // User cancelled
      });
    },
    
    async saveTissueTypes() {
      // Final validation check
      if (this.tissueTypeEditErrors.length > 0) {
        this.$notify({
          title: 'Validation Error',
          message: 'Please fix all validation errors before saving',
          type: 'error',
          position: 'top-right'
        });
        return;
      }
      
      this.savingTissueTypes = true;
      
      try {
        // Convert editable tissue types to simple array of names
        const tissueTypesList = this.editableTissueTypes.map(item => item.name.trim());
        
        const res = await fetch('/api/projectUpload/update_tissue_types', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tissue_types: tissueTypesList })
        }).then(r => r.json());
        
        if (res && res.code === 200) {
          this.$notify({
            title: 'Success',
            message: 'Tissue types updated successfully',
            type: 'success',
            position: 'top-right'
          });
          
          // Update the options list
          this.tissueTypeOptions = res.data.tcga_tissues || [];

          // deselect tissue types from el-select
          this.form.tissueType = '';
          
          // Close dialog
          this.manageTissueTypesDialog = false;
        } else {
          this.$notify({
            title: 'Error',
            message: res.error || 'Failed to update tissue types',
            type: 'error',
            position: 'top-right'
          });
        }
      } catch (e) {
        console.error('Error updating tissue types:', e);
        this.$notify({
          title: 'Error',
          message: 'Failed to update tissue types',
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.savingTissueTypes = false;
      }
    },
    
    handleCancerAbbrInput(value) {
      // Only allow A-Z letters and convert to uppercase
      const filtered = value.replace(/[^A-Za-z]/g, '').toUpperCase();
      this.newCancerTypeAbbr = filtered;
    },
    
    async addNewCancerType() {
      // Validate inputs
      if (!this.newCancerTypeAbbr || !this.newCancerTypeAbbr.trim()) {
        this.$notify({
          title: 'Error',
          message: 'Please enter a cancer type abbreviation',
          type: 'error',
          position: 'top-right'
        });
        return;
      }
      
      if (!this.newCancerTypeFullName || !this.newCancerTypeFullName.trim()) {
        this.$notify({
          title: 'Error',
          message: 'Please enter a cancer type full name',
          type: 'error',
          position: 'top-right'
        });
        return;
      }
      
      if (this.cancerAbbrExists || this.cancerFullNameExists) {
        this.$notify({
          title: 'Error',
          message: 'The abbreviation or full name already exists',
          type: 'error',
          position: 'top-right'
        });
        return;
      }
      
      this.addingCancerType = true;
      
      try {
        // Combine abbreviation and full name in format: "ABBR_FullName" to match existing format
        const cancerTypeName = `${this.newCancerTypeAbbr.trim()}_${this.newCancerTypeFullName.trim()}`;
        
        const res = await fetch('/api/projectUpload/add_cancer_type', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cancer_type: cancerTypeName })
        }).then(r => r.json());
        
        if (res && res.code === 200) {
          this.$notify({
            title: 'Success',
            message: `Cancer type "${cancerTypeName}" added successfully`,
            type: 'success',
            position: 'top-right'
          });
          
          // Update the options list
          this.cancerTypeOptions = res.data.tcga_names || [];
          
          // Set the newly added type as selected
          this.form.cancerType = cancerTypeName;
          
          // Close dialog
          this.addCancerTypeDialog = false;
          this.newCancerType = '';
          this.newCancerTypeAbbr = '';
          this.newCancerTypeFullName = '';
        } else {
          this.$notify({
            title: 'Error',
            message: res.error || 'Failed to add cancer type',
            type: 'error',
            position: 'top-right'
          });
        }
      } catch (e) {
        console.error('Error adding cancer type:', e);
        this.$notify({
          title: 'Error',
          message: 'Failed to add cancer type',
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.addingCancerType = false;
      }
    },
    
    async addNewTissueType() {
      // Validate input
      if (!this.newTissueType || !this.newTissueType.trim()) {
        this.$notify({
          title: 'Error',
          message: 'Please enter a tissue type name',
          type: 'error',
          position: 'top-right'
        });
        return;
      }
      
      if (this.tissueNameExists) {
        this.$notify({
          title: 'Error',
          message: 'This tissue type already exists',
          type: 'error',
          position: 'top-right'
        });
        return;
      }
      
      this.addingTissueType = true;
      
      try {
        const tissueTypeName = this.newTissueType.trim();
        
        const res = await fetch('/api/projectUpload/add_tissue_type', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tissue_type: tissueTypeName })
        }).then(r => r.json());
        
        if (res && res.code === 200) {
          this.$notify({
            title: 'Success',
            message: `Tissue type "${tissueTypeName}" added successfully`,
            type: 'success',
            position: 'top-right'
          });
          
          // Update the options list
          this.tissueTypeOptions = res.data.tcga_tissues || [];
          
          // Set the newly added type as selected
          this.form.tissueType = tissueTypeName;
          
          // Close dialog
          this.addTissueTypeDialog = false;
          this.newTissueType = '';
        } else {
          this.$notify({
            title: 'Error',
            message: res.error || 'Failed to add tissue type',
            type: 'error',
            position: 'top-right'
          });
        }
      } catch (e) {
        console.error('Error adding tissue type:', e);
        this.$notify({
          title: 'Error',
          message: 'Failed to add tissue type',
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.addingTissueType = false;
      }
    },
    
    nextStep() {
      if (this.currentStep === 1 && !this.canProceedFromStep2) {
        let message = 'Please fill in all required fields: Name, Description, Cancer Type, and Tissue Type';
        if (this.form.datasetType === 'public' || this.form.datasetType === 'differential') {
          message += '. Also, PubMed ID is required - please load from PubMed or enable manual input.';
        }
        this.$notify({
          title: 'Required Fields Missing',
          message: message,
          type: 'warning',
          position: 'top-right'
        });
        return;
      }
      if (this.currentStep < 2) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    goToStep(step) {
      // Allow going back to any previous step or staying on current step
      if (step <= this.currentStep) {
        this.currentStep = step;
      }
    },
    async proceedToQC() {
      if (!this.allFilesValid) {
        this.$notify({
          title: 'Warning',
          message: 'Please validate all files first',
          type: 'warning',
          position: 'top-right'
        });
        return;
      }
      
      this.currentStep = 3;
      await this.loadQCStats();
    },
    async loadQCStats() {
      this.qcLoading = true;
      this.qcStats = null;
      
      try {
        const formData = new FormData();
        formData.append('exp_file', this.expFile);
        formData.append('obs_file', this.obsFile);
        formData.append('var_file', this.varFile);
        
        const res = await fetch('/api/projectUpload/get_qc_stats', {
          method: 'POST',
          body: formData
        }).then(r => r.json());
        
        if (res.code === 200) {
          this.qcStats = res.data.qc_stats;
          
          // Render charts after data is loaded
          this.$nextTick(() => {
            this.renderQCCharts();
            this.previewQCFilter();
          });
          
          this.$notify({
            title: 'Success',
            message: 'QC statistics loaded successfully',
            type: 'success',
            position: 'top-right'
          });
        } else {
          this.$notify({
            title: 'Error',
            message: 'Failed to load QC statistics',
            type: 'error',
            position: 'top-right'
          });
        }
      } catch (e) {
        this.$notify({
          title: 'Error',
          message: 'Failed to load QC statistics: ' + e.message,
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.qcLoading = false;
      }
    },
    renderQCCharts() {
      console.log('Rendering QC charts with Plotly...');
      console.log('QC Stats:', this.qcStats);
      console.log('Apply log1p:', this.applyLog1p);
      
      // Render total counts histogram
      if (this.qcStats.total_counts_per_cell && this.qcStats.total_counts_per_cell.length > 0) {
        // Use pre-calculated log1p total counts from backend if available and toggle is on
        let data;
        if (this.applyLog1p && this.qcStats.log1p_total_counts_per_cell && this.qcStats.log1p_total_counts_per_cell.length > 0) {
          data = this.qcStats.log1p_total_counts_per_cell;
        } else {
          data = this.qcStats.total_counts_per_cell;
        }
        
        this.totalCountsPlot = {
          data: [{
            x: data,
            type: 'histogram',
            nbinsx: 50,
            marker: {
              color: this.applyLog1p ? 'rgba(34, 139, 34, 0.8)' : 'rgba(70, 130, 180, 0.8)',
              line: { color: 'rgba(0, 0, 0, 0.3)', width: 1 }
            },
            hovertemplate: '%{x:.2f}: %{y}<extra></extra>'
          }],
          layout: {
            xaxis: {
              title: this.applyLog1p ? 'Total Counts (log1p matrix)' : 'Total Counts',
              automargin: true
            },
            yaxis: {
              title: 'Frequency',
              automargin: true
            },
            margin: { t: 20, r: 20, b: 60, l: 60 },
            bargap: 0.05
          }
        };
      } else {
        this.totalCountsPlot = { data: [], layout: {} };
      }
      
      // Render dropout histogram (not affected by log1p toggle)
      if (this.qcStats.pct_dropout_per_gene && this.qcStats.pct_dropout_per_gene.length > 0) {
        this.dropoutPlot = {
          data: [{
            x: this.qcStats.pct_dropout_per_gene,
            type: 'histogram',
            nbinsx: 50,
            marker: {
              color: 'rgba(255, 140, 0, 0.8)',
              line: { color: 'rgba(0, 0, 0, 0.3)', width: 1 }
            },
            hovertemplate: '%{x:.2f}%: %{y}<extra></extra>'
          }],
          layout: {
            xaxis: {
              title: 'Dropout %',
              automargin: true,
              range: [0, 100]  // Fixed range to handle all-zero data
            },
            yaxis: {
              title: 'Frequency',
              automargin: true
            },
            margin: { t: 20, r: 20, b: 60, l: 60 },
            bargap: 0.05
          }
        };
      } else {
        this.dropoutPlot = { data: [], layout: {} };
      }
    },
    updateChartsWithLog() {
      // Re-render charts with or without log transformation
      this.renderQCCharts();
    },
    async previewQCFilter() {
      if (!this.expFile || !this.obsFile || !this.varFile) return;
      
      try {
        const formData = new FormData();
        formData.append('exp_file', this.expFile);
        formData.append('obs_file', this.obsFile);
        formData.append('var_file', this.varFile);
        formData.append('min_genes', this.qcParams.min_genes);
        formData.append('min_cells', this.qcParams.min_cells);
        formData.append('max_dropout', this.qcParams.max_dropout);
        
        const res = await fetch('/api/projectUpload/preview_qc_filter', {
          method: 'POST',
          body: formData
        }).then(r => r.json());
        
        if (res.code === 200) {
          this.qcPreview = res.data;
        }
      } catch (e) {
        console.error('Failed to preview QC filter:', e);
      }
    },
    async fetchPubmed() {
      if (this.form.datasetType !== 'public' && this.form.datasetType !== 'differential') return;
      this.pubmedLoaded = false;
      this.manualInput = false;
      const pmid = String(this.form.pubmedId || '').trim();
      
      if (!pmid) {
        this.$notify({
          title: 'Warning',
          message: 'Please enter a PubMed ID',
          type: 'warning',
          position: 'top-right'
        });
        return;
      }
      
      this.pubmedLoading = true;
      
      try {
        const res = await fetch('/api/projectUpload/pubmed_lookup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pubmedId: pmid })
        }).then(r => r.json());
        if (res && res.code === 200 && res.data && res.data.valid) {
          if (res.data.title) this.form.name = res.data.title;
          if (res.data.abstract) this.form.description = res.data.abstract;
          this.pubmedLoaded = true;
          this.$notify({
            title: 'Success',
            message: 'PubMed metadata loaded',
            type: 'success',
            position: 'top-right'
          });
        } else {
          this.$notify({
            title: 'Warning',
            message: 'Unable to fetch information from PubMed. Please use Manual Input.',
            type: 'warning',
            position: 'top-right'
          });
        }
      } catch (e) {
        this.$notify({
          title: 'Error',
          message: 'Failed to fetch PubMed metadata: ' + e.message,
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.pubmedLoading = false;
      }
    },
    enableManualInput() {
      if (!this.form.pubmedId || !this.form.pubmedId.trim()) {
        this.$notify({
          title: 'Warning',
          message: 'Please enter a PubMed ID first',
          type: 'warning',
          position: 'top-right'
        });
        return;
      }
      this.manualInput = true;
      this.pubmedLoaded = false;
      this.form.name = '';
      this.form.description = '';
      this.$notify({
        title: 'Success',
        message: 'Manual input enabled. Please enter title and abstract.',
        type: 'success',
        position: 'top-right'
      });
    },
    handleJsonUpload(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          
          // Load data from JSON into form
          if (jsonData.pubmedId) this.form.pubmedId = jsonData.pubmedId;
          if (jsonData.name || jsonData.title) this.form.name = jsonData.name || jsonData.title;
          if (jsonData.description || jsonData.abstract) this.form.description = jsonData.description || jsonData.abstract;
          
          // Validate cancer type against controlled list
          if (jsonData.cancerType) {
            if (this.cancerTypeOptions.includes(jsonData.cancerType)) {
              this.form.cancerType = jsonData.cancerType;
            } else {
              console.warn('Cancer type not in controlled list:', jsonData.cancerType);
            }
          }
          
          // Validate tissue type against controlled list
          if (jsonData.tissueType) {
            if (this.tissueTypeOptions.includes(jsonData.tissueType)) {
              this.form.tissueType = jsonData.tissueType;
            } else {
              console.warn('Tissue type not in controlled list:', jsonData.tissueType);
            }
          }
          
          if (jsonData.datasetType) {
            this.form.datasetType = jsonData.datasetType;
          }
          if (jsonData.basicInfo && Array.isArray(jsonData.basicInfo)) {
            this.form.basicInfo = jsonData.basicInfo.map(item => ({
              key: item.key || item.name,
              value: item.value
            }));
          }
          
          this.manualInput = true;
          this.pubmedLoaded = false;
          this.$notify({
            title: 'Success',
            message: 'Dataset metadata loaded from file',
            type: 'success',
            position: 'top-right'
          });
        } catch (error) {
          this.$notify({
            title: 'Error',
            message: 'Failed to parse file: ' + error.message,
            type: 'error',
            position: 'top-right'
          });
        }
      };
      reader.readAsText(file.raw);
    },
    addBasicInfo() {
      const key = (this.basicInfoKey || '').trim();
      const value = (this.basicInfoValue || '').trim();
      if (!key || !value) {
        this.$notify({
          title: 'Warning',
          message: 'Please enter both key and value',
          type: 'warning',
          position: 'top-right'
        });
        return;
      }
      this.form.basicInfo.push({ key, value });
      this.basicInfoKey = '';
      this.basicInfoValue = '';
      this.showBasicInfoDialog = false;
      this.$notify({
        title: 'Success',
        message: 'Added',
        type: 'success',
        position: 'top-right'
      });
    },
    removeBasicInfo(index) {
      this.form.basicInfo.splice(index, 1);
    },
    handleBasicInfoDragStart(index, event) {
      this.draggingBasicInfoIndex = index;
      this.draggedBasicInfoItem = this.form.basicInfo[index];
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', event.target.innerHTML);
    },
    handleBasicInfoDragOver(index, event) {
      if (index !== this.draggingBasicInfoIndex) {
        event.dataTransfer.dropEffect = 'move';
      }
    },
    handleBasicInfoDrop(index, event) {
      event.preventDefault();
      if (this.draggingBasicInfoIndex !== null && this.draggingBasicInfoIndex !== index) {
        // Remove item from old position
        const draggedItem = this.form.basicInfo[this.draggingBasicInfoIndex];
        this.form.basicInfo.splice(this.draggingBasicInfoIndex, 1);
        
        // Insert at new position
        // Adjust index if dragging from before to after
        const newIndex = this.draggingBasicInfoIndex < index ? index - 1 : index;
        this.form.basicInfo.splice(newIndex, 0, draggedItem);
      }
    },
    handleBasicInfoDragEnd() {
      this.draggingBasicInfoIndex = null;
      this.draggedBasicInfoItem = null;
    },
    autoPopulateBasicInfo() {
      // Remove any existing auto-generated basic info first
      this.form.basicInfo = this.form.basicInfo.filter(item =>
        ![
          'Sample Size',
          'Number of Proteins',
          'Number of Clinical Variables',
          'Total Data Points',
          'Protein Annotation Columns',
          'Data Density',
          'Tumor Sample Count',
          'NT Sample Count',
          'NAT Sample Count'
        ].includes(item.key)
      );

      // 1. Add Number of Proteins (from var file - number of proteins)
      if (this.varFileStatus.shape && this.varFileStatus.shape[0]) {
        this.form.basicInfo.push({
          key: 'Number of Proteins',
          value: String(this.varFileStatus.shape[0])
        });
      }

      // 2. Add Number of Clinical Variables (from obs file - number of columns)
      if (this.obsFileStatus.shape && this.obsFileStatus.shape[1]) {
        this.form.basicInfo.push({
          key: 'Number of Clinical Variables',
          value: String(this.obsFileStatus.shape[1])
        });
      }

      // 3. Add Sample Size
      this.updateSampleSize();

      // 4. Add Sample Type Counts
      this.addSampleTypeCounts();

    },
    updateSampleSize() {
      // Remove existing Sample Size
      this.form.basicInfo = this.form.basicInfo.filter(item => item.key !== 'Sample Size');

      // Calculate and add Sample Size
      if (this.obsFileStatus.shape && this.obsFileStatus.shape[0]) {
        const numRows = this.obsFileStatus.shape[0];
        // Sample size is simply the number of rows (samples)
        const sampleSize = numRows;
        this.form.basicInfo.push({
          key: 'Sample Size',
          value: String(sampleSize)
        });
      }
    },
    addSampleTypeCounts() {
      // Remove existing Sample Type counts
      this.form.basicInfo = this.form.basicInfo.filter(item =>
        !['Tumor Sample Count', 'NT Sample Count', 'NAT Sample Count'].includes(item.key)
      );

      // Add Sample Type counts if available
      if (this.obsFileStatus.sampleTypeCounts) {
        const counts = this.obsFileStatus.sampleTypeCounts;

        // Always show all three types, even if count is 0
        this.form.basicInfo.push({
          key: 'Tumor Sample Count',
          value: String(counts.Tumor || 0)
        });

        this.form.basicInfo.push({
          key: 'NT Sample Count',
          value: String(counts.NT || 0)
        });

        this.form.basicInfo.push({
          key: 'NAT Sample Count',
          value: String(counts.NAT || 0)
        });
      }
    },
    submit() {
      // Placeholder: validation handled by backend on submit
      // eslint-disable-next-line no-console
      console.log('Form payload', this.form);
      this.$notify({
        title: 'Success',
        message: 'Ready to submit (backend will validate)',
        type: 'success',
        position: 'top-right'
      });
    },
    reset() {
      this.currentStep = 0;
      this.form = {
        name: '',
        description: '',
        pubmedId: '',
        cancerType: '',
        tissueType: '',
        datasetType: null,
        basicInfo: []
      };
      this.pubmedLoaded = false;
      this.manualInput = false;
      // this.isPairedAnalysis = false;
      this.resetFileUploads();
    },
    resetFileUploads() {
      this.expFile = null;
      this.obsFile = null;
      this.varFile = null;
      this.expFileList = [];
      this.obsFileList = [];
      this.varFileList = [];
      this.expFileStatus = { valid: false, type: '', text: '', shape: null };
      this.obsFileStatus = { valid: false, type: '', text: '', shape: null };
      this.varFileStatus = { valid: false, type: '', text: '', shape: null };
      this.fileErrors = [];
    },
    resetAllFiles() {
      this.$confirm('Are you sure you want to reset all uploaded files? This will clear all file validations and you will need to upload and validate files again.', 'Reset All Files', {
        confirmButtonText: 'Yes, Reset',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.resetFileUploads();
        
        // Clear the upload components
        if (this.$refs.expUpload) {
          this.$refs.expUpload.clearFiles();
        }
        if (this.$refs.obsUpload) {
          this.$refs.obsUpload.clearFiles();
        }
        if (this.$refs.varUpload) {
          this.$refs.varUpload.clearFiles();
        }
        
        this.$notify({
          title: 'Success',
          message: 'All files have been reset',
          type: 'success',
          position: 'top-right'
        });
      }).catch(() => {
        // User cancelled
      });
    },
    
    // File handlers
    handleExpChange(file, fileList) {
      this.expFile = file.raw;
      this.expFileList = fileList;
      this.expFileStatus = { valid: false, type: '', text: '', shape: null };
      this.fileErrors = [];
    },
    handleExpRemove() {
      // Prevent removal if file is validated
      if (this.expFileStatus.valid) {
        this.$notify({
          title: 'Cannot Remove',
          message: 'Expression file is validated and locked. Use "Reset All Files" button to reset.',
          type: 'warning',
          position: 'top-right'
        });
        return false; // Prevent removal
      }
      this.expFile = null;
      this.expFileList = [];
      this.expFileStatus = { valid: false, type: '', text: '', shape: null };
      this.fileErrors = [];
    },
    handleObsChange(file, fileList) {
      this.obsFile = file.raw;
      this.obsFileList = fileList;
      this.obsFileStatus = { valid: false, type: '', text: '', shape: null };
      this.fileErrors = [];
    },
    handleObsRemove() {
      this.obsFile = null;
      this.obsFileList = [];
      this.obsFileStatus = { valid: false, type: '', text: '', shape: null };
      this.fileErrors = [];
    },
    handleVarChange(file, fileList) {
      this.varFile = file.raw;
      this.varFileList = fileList;
      this.varFileStatus = { valid: false, type: '', text: '', shape: null };
      this.fileErrors = [];
    },
    handleVarRemove() {
      this.varFile = null;
      this.varFileList = [];
      this.varFileStatus = { valid: false, type: '', text: '', shape: null };
      this.fileErrors = [];
    },
    
    handleSliceChange(file, fileList) {
      this.sliceFile = file.raw;
      this.sliceFileList = fileList;
    },
    
    handleSliceRemove() {
      this.sliceFile = null;
      this.sliceFileList = [];
    },
    
    // Differential expression file handlers
    handleDiffExpChange(file, fileList) {
      this.diffExpFile = file.raw;
      this.diffExpFileList = fileList;
      this.expFileStatus = { valid: false, type: '', text: '', shape: null };
      this.fileErrors = [];
    },
    
    handleDiffExpRemove() {
      this.diffExpFile = null;
      this.diffExpFileList = [];
      this.expFileStatus = { valid: false, type: '', text: '', shape: null };
      this.fileErrors = [];
    },
    
    // File validation
    async checkExpFile() {
      if (!this.expFile) return;
      
      this.expChecking = true;
      this.fileErrors = [];
      
      try {
        const formData = new FormData();
        formData.append('file', this.expFile);
        
        const res = await fetch('/api/projectUpload/validate_exp_file', {
          method: 'POST',
          body: formData
        }).then(r => r.json());
        
        if (res.code === 200 && res.data.valid) {
          this.expFileStatus = {
            valid: true,
            type: 'success',
            text: 'Valid',
            shape: res.data.shape
          };
          this.$notify({
            title: 'Success',
            message: 'Expression file validated successfully',
            type: 'success',
            position: 'top-right'
          });
          
          // Show warnings if any
          if (res.data.warnings && res.data.warnings.length > 0) {
            const warnings = res.data.warnings.filter(w => w.includes('[WARN]'));
            if (warnings.length > 0) {
              this.fileErrors = warnings;
            }
          }
        } else {
          this.expFileStatus = {
            valid: false,
            type: 'danger',
            text: 'Invalid',
            shape: null
          };
          this.fileErrors = res.data.warnings || [res.error || 'Validation failed'];
          this.$notify({
            title: 'Error',
            message: 'Expression file validation failed',
            type: 'error',
            position: 'top-right'
          });
        }
      } catch (e) {
        this.expFileStatus = { valid: false, type: 'danger', text: 'Error', shape: null };
        this.fileErrors = [`[ERROR] ${e.message}`];
        this.$notify({
          title: 'Error',
          message: 'Failed to validate file: ' + e.message,
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.expChecking = false;
      }
    },
    
    async checkObsFile() {
      if (!this.obsFile || !this.expFile) return;
      
      this.obsChecking = true;
      this.fileErrors = [];
      
      try {
        const formData = new FormData();
        formData.append('obs_file', this.obsFile);
        formData.append('exp_file', this.expFile);
        
        const res = await fetch('/api/projectUpload/validate_obs_file', {
          method: 'POST',
          body: formData
        }).then(r => r.json());
        
        if (res.code === 200 && res.data.valid) {
          this.obsFileStatus = {
            valid: true,
            type: 'success',
            text: 'Valid',
            shape: res.data.shape,
            sampleTypeCounts: res.data.sample_type_counts || {}
          };
          this.$notify({
            title: 'Success',
            message: 'Obs file validated successfully',
            type: 'success',
            position: 'top-right'
          });
          
          // Show warnings if any
          if (res.data.warnings && res.data.warnings.length > 0) {
            const warnings = res.data.warnings.filter(w => w.includes('[WARN]'));
            if (warnings.length > 0) {
              this.fileErrors = warnings;
            }
          }
        } else {
          this.obsFileStatus = {
            valid: false,
            type: 'danger',
            text: 'Invalid',
            shape: null
          };
          this.fileErrors = res.data.warnings || [res.error || 'Validation failed'];
          this.$notify({
            title: 'Error',
            message: 'Obs file validation failed',
            type: 'error',
            position: 'top-right'
          });
        }
      } catch (e) {
        this.obsFileStatus = { valid: false, type: 'danger', text: 'Error', shape: null };
        this.fileErrors = [`[ERROR] ${e.message}`];
        this.$notify({
          title: 'Error',
          message: 'Failed to validate file: ' + e.message,
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.obsChecking = false;
      }
    },
    
    async checkVarFile() {
      if (!this.varFile || !this.expFile) return;
      
      this.varChecking = true;
      this.fileErrors = [];
      
      try {
        const formData = new FormData();
        formData.append('var_file', this.varFile);
        formData.append('exp_file', this.expFile);
        
        const res = await fetch('/api/projectUpload/validate_var_file', {
          method: 'POST',
          body: formData
        }).then(r => r.json());
        
        if (res.code === 200 && res.data.valid) {
          this.varFileStatus = {
            valid: true,
            type: 'success',
            text: 'Valid',
            shape: res.data.shape
          };
          this.$notify({
            title: 'Success',
            message: 'Protein metadata file validated successfully',
            type: 'success',
            position: 'top-right'
          });
          
          // Show warnings if any
          if (res.data.warnings && res.data.warnings.length > 0) {
            const warnings = res.data.warnings.filter(w => w.includes('[WARN]'));
            if (warnings.length > 0) {
              this.fileErrors = warnings;
            }
          }
        } else {
          this.varFileStatus = {
            valid: false,
            type: 'danger',
            text: 'Invalid',
            shape: null
          };
          this.fileErrors = res.data.warnings || [res.error || 'Validation failed'];
          this.$notify({
            title: 'Error',
            message: 'Protein metadata file validation failed',
            type: 'error',
            position: 'top-right'
          });
        }
      } catch (e) {
        this.varFileStatus = { valid: false, type: 'danger', text: 'Error', shape: null };
        this.fileErrors = [`[ERROR] ${e.message}`];
        this.$notify({
          title: 'Error',
          message: 'Failed to validate file: ' + e.message,
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.varChecking = false;
      }
    },
    
    async checkDiffExpFile() {
      if (!this.diffExpFile) return;
      
      this.expChecking = true;
      this.fileErrors = [];
      
      try {
        const formData = new FormData();
        formData.append('file', this.diffExpFile);
        
        const res = await fetch('/api/projectUpload/validate_diff_exp_file', {
          method: 'POST',
          body: formData
        }).then(r => r.json());
        
        if (res.code === 200 && res.data.valid) {
          this.expFileStatus = {
            valid: true,
            type: 'success',
            text: 'Valid',
            shape: res.data.shape
          };
          this.$notify({
            title: 'Success',
            message: 'Differential expression file validated successfully',
            type: 'success',
            position: 'top-right'
          });
          
          // Show warnings if any
          if (res.data.warnings && res.data.warnings.length > 0) {
            const warnings = res.data.warnings.filter(w => w.includes('[WARN]'));
            if (warnings.length > 0) {
              this.fileErrors = warnings;
            }
          }
        } else {
          this.expFileStatus = {
            valid: false,
            type: 'error',
            text: 'Invalid',
            shape: null
          };
          this.fileErrors = [res.error || 'File validation failed'];
          this.$notify({
            title: 'Error',
            message: 'Differential expression file validation failed',
            type: 'error',
            position: 'top-right'
          });
        }
      } catch (e) {
        this.fileErrors = [`[ERROR] ${e.message}`];
        this.$notify({
          title: 'Error',
          message: 'Failed to validate file: ' + e.message,
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.expChecking = false;
      }
    },
    
    async uploadDataset() {
      // Check if files are valid based on dataset type
      const isDifferential = this.form.datasetType === 'differential';
      const filesValid = isDifferential ? this.expFileStatus.valid : this.allFilesValid;
      
      if (!filesValid) {
        this.$notify({
          title: 'Warning',
          message: 'Please validate all files first',
          type: 'warning',
          position: 'top-right'
        });
        return;
      }
      
      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadProgressText = 'Preparing upload...';
      this.fileErrors = [];
      
      try {
        // Prepare metadata from form
        const metadata = {
          name: this.form.name,
          description: this.form.description,
          pubmedId: this.form.pubmedId,
          cancerType: this.form.cancerType,
          tissueType: this.form.tissueType,
          datasetType: this.form.datasetType,
          basicInfo: this.form.basicInfo.map(item => ({
            name: item.key,
            value: item.value
          }))
        };
        
        // Create FormData with files
        const formData = new FormData();
        
        if (isDifferential) {
          // For differential data, only upload the differential expression file
          formData.append('diff_exp_file', this.diffExpFile);
          formData.append('metadata', JSON.stringify(metadata));
        } else {
          // For regular datasets, upload all files with QC params
          const qc_params = {
              ...this.qcParams,
              apply_log1p: this.applyLog1p
          };
          
          formData.append('exp_file', this.expFile);
          formData.append('obs_file', this.obsFile);
          formData.append('var_file', this.varFile);
          
          // Add slice file if present (only for private datasets)
          if (this.form.datasetType === 'private' && this.sliceFile) {
            formData.append('slice_file', this.sliceFile);
          }
          
          formData.append('metadata', JSON.stringify(metadata));
          formData.append('qc_params', JSON.stringify(qc_params));
        }
        
        // Use XMLHttpRequest for upload progress tracking
        const xhr = new XMLHttpRequest();
        
        // Track upload progress
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 80); // Reserve 20% for processing
            this.uploadProgress = percentComplete;
            this.uploadProgressText = 'Uploading files...';
          }
        });
        
        // Create promise to handle xhr
        const uploadPromise = new Promise((resolve, reject) => {
          xhr.onload = () => {
            if (xhr.status === 200) {
              try {
                this.uploadProgress = 85;
                this.uploadProgressText = 'Processing data...';
                resolve(JSON.parse(xhr.responseText));
              } catch (e) {
                reject(new Error('Failed to parse response'));
              }
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          };
          
          xhr.onerror = () => reject(new Error('Network error during upload'));
          
          // Use different endpoint for differential data
          const endpoint = isDifferential ? '/api/projectUpload/upload_differential_dataset' : '/api/projectUpload/upload_dataset';
          xhr.open('POST', endpoint);
          xhr.send(formData);
        });
        
        const res = await uploadPromise;
        
        // Simulate processing progress
        this.uploadProgress = 90;
        this.uploadProgressText = 'Finalizing...';
        await new Promise(resolve => setTimeout(resolve, 500));
        
        this.uploadProgress = 100;
        this.uploadProgressText = 'Upload complete!';
        
        if (res.code === 200) {
          const datasetName = res.data.dataset_name;
          const warnings = res.data.warnings || [];
          
          // Show success with warnings if any
          let message = `Dataset uploaded successfully as ${datasetName}`;
          if (warnings.length > 0) {
            const infoWarnings = warnings.filter(w => w.includes('[INFO]') || w.includes('[SUCCESS]'));
            console.log('Upload info:', infoWarnings);
          }
          
          this.$notify({
            title: 'Success',
            message: message,
            type: 'success',
            position: 'top-right'
          });
          
          // Optionally redirect to the project detail page
          this.$confirm(`Dataset ${datasetName} has been created. Would you like to view it now?`, 'Success', {
            confirmButtonText: 'View Dataset',
            cancelButtonText: 'Upload Another',
            type: 'success'
          }).then(() => {
            const routeData = this.$router.resolve(`/project/${datasetName}`);
            window.open(routeData.href, '_blank');
          }).catch(() => {
            this.reset();
          });
        } else {
          this.fileErrors = [res.error || 'Upload failed'];
          this.$notify({
            title: 'Error',
            message: 'Dataset upload failed',
            type: 'error',
            position: 'top-right'
          });
        }
      } catch (e) {
        this.fileErrors = [`[ERROR] ${e.message}`];
        this.$notify({
          title: 'Error',
          message: 'Failed to upload dataset: ' + e.message,
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.uploading = false;
        // Reset progress after a delay
        setTimeout(() => {
          this.uploadProgress = 0;
          this.uploadProgressText = 'Preparing upload...';
        }, 2000);
      }
    }
  }
}
</script>

<style scoped>
/* App Container */
.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f8fafc;
  overflow: hidden;
}

/* Navigation Bar */
.navigate-bar {
  background-color: #ffffff;
  padding: 20px 32px;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.page-title-section {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.steps-container {
  max-width: 800px;
  margin: 0 auto;
}

.clickable-steps .el-step {
  cursor: pointer;
}

.clickable-steps .el-step.is-finish {
  cursor: pointer;
}

.clickable-steps .el-step.is-finish:hover .el-step__title {
  color: #3b82f6;
}

.clickable-steps .el-step.is-process {
  cursor: default;
}

/* Content Section */
.content-section {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
}

.content-section .el-card {
  max-width: 1200px;
  margin: 0 auto;
}

/* Form Styles */
.form-body .field { margin-bottom: 16px; }
.form-body .label { font-size: 13px; color: #64748b; margin-bottom: 6px; }
.row-line { display: flex; gap: 10px; align-items: center; }
.row-line .el-input { flex: 1; }

/* Paired Analysis Field Removed */
/*.paired-analysis-field {
  background: #f0f9ff;
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
}*/

.field-hint {
  font-size: 12px;
  color: #64748b;
  margin-top: 8px;
  margin-left: 24px;
  font-style: italic;
}

/* Step Styles */
.step-header {
  margin-bottom: 32px;
  text-align: center;
}

.step-header h3 {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.step-header p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.step-actions {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

/* Upload Progress Bar - Minimal Inline Style */
.upload-progress-inline {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
  max-width: 300px;
}

.progress-text {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

.privacy-selection .el-radio-group {
  margin: 20px;
  gap: 12px;
}

.privacy-selection .el-radio-group .el-radio {
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

/* File upload section */
.file-upload-section {
  background: #f8fafc;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.file-upload-item {
  background: white;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  border: 1px solid #e2e8f0;
}

.file-upload-item:last-child {
  margin-bottom: 0;
}

/* Override Element UI upload dragger width constraint - force 100% width */
.file-upload-item .el-upload,
.file-upload-item .el-upload-dragger {
  width: 100% !important;
  max-width: 100% !important;
}

.file-upload-item >>> .el-upload,
.file-upload-item >>> .el-upload-dragger {
  width: 100% !important;
  max-width: 100% !important;
}

.file-upload-item /deep/ .el-upload,
.file-upload-item /deep/ .el-upload-dragger {
  width: 100% !important;
  max-width: 100% !important;
}

/* File list width adjusts automatically based on filename length */
.file-upload-item .el-upload-list,
.file-upload-item >>> .el-upload-list,
.file-upload-item /deep/ .el-upload-list {
  width: fit-content !important;
  min-width: 200px !important;
  max-width: 80% !important;
}

.file-upload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.file-label {
  font-weight: 500;
  color: #334155;
  font-size: 14px;
}

.file-info {
  margin-top: 8px;
  font-size: 13px;
  color: #64748b;
  padding-left: 4px;
}

.error-item {
  font-size: 13px;
  line-height: 1.6;
  color: #dc2626;
}

.modern-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: #ffffff;
  transition: all 0.2s ease;
}

.modern-btn:hover {
  opacity: 0.95;
}

.manual-btn {
  background: #e38c28;
  color: #ffffff;
  border: none;
  transition: all 0.2s ease;
}

.manual-btn:hover {
  opacity: 0.95;
}

.json-btn {
  background: #10b981;
  color: #ffffff;
  border: none;
  transition: all 0.2s ease;
}

.json-btn:hover {
  opacity: 0.95;
}

/* QC Section Styles */
.qc-section {
  margin-bottom: 24px;
  border: 1px solid #e2e8f0;
}

.qc-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.qc-section-header-important {
  font-size: 18px;
  color: #1e40af;
}

.qc-section-header i {
  font-size: 18px;
  color: #3b82f6;
}

.qc-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.qc-stat-item {
  background: #f8fafc;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #e2e8f0;
}

.qc-stat-label {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 8px;
}

.qc-stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
}

.qc-charts-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
}

.qc-chart {
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.qc-chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  text-align: center;
}


.param-hint {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-left: 12px;
  font-style: italic;
}

.qc-params-container {
  background: #f8fafc;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.qc-form-item {
  margin-bottom: 24px;
}

.qc-input-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.param-hint-inline {
  font-size: 13px;
  color: #475569;
  font-style: italic;
  flex: 1;
}

.qc-preview-content {
  margin-top: 8px;
}

.qc-preview-item {
  font-size: 14px;
  margin-bottom: 4px;
}

.qc-preview-retain {
  color: #059669;
  font-weight: 600;
}

.qc-preview-remove {
  color: #dc2626;
  font-weight: 600;
}

.required-star {
  color: #f56c6c;
  font-weight: bold;
  margin-left: 2px;
}

/* Disabled/locked upload styling */
.file-upload-item .el-upload.is-disabled .el-upload-dragger {
  background-color: #f5f7fa;
  border-color: #67C23A;
  cursor: not-allowed;
}

.file-upload-item .el-upload.is-disabled .el-upload-dragger:hover {
  border-color: #67C23A;
}

/* Draggable tag styling */
.draggable-tag {
  cursor: move !important;
  transition: all 0.3s ease;
  user-select: none;
}

.draggable-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.draggable-tag.dragging {
  opacity: 0.5;
  transform: scale(0.95);
}

.draggable-tag:active {
  cursor: grabbing !important;
}
</style>

<style>
/* Global styles for manage cancer types dialog (not scoped) */
.manage-cancer-types-dialog {
  display: flex;
  flex-direction: column;
}

.manage-cancer-types-dialog .el-dialog__body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.manage-cancer-types-dialog .el-table {
  flex: 1;
}

/* Global styles for manage tissue types dialog (not scoped) */
.manage-tissue-types-dialog {
  display: flex;
  flex-direction: column;
}

.manage-tissue-types-dialog .el-dialog__body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.manage-tissue-types-dialog .el-table {
  flex: 1;
}
</style>



